<template>
  <div 
    class="accessibility-statement"
    role="main"
    aria-labelledby="accessibility-title"
  >
    <!-- Page Header -->
    <header class="statement-header">
      <h1 id="accessibility-title">
        {{ $t('accessibility.statement.title', 'Accessibility Statement') }}
      </h1>
      <p class="statement-intro">
        {{ $t('accessibility.statement.intro', 'This accessibility statement applies to the GOZ-App website and gaming platform.') }}
      </p>
    </header>

    <!-- Main Content -->
    <main class="statement-content">
      <!-- Compliance Section -->
      <section 
        aria-labelledby="compliance-heading"
        class="statement-section"
      >
        <h2 id="compliance-heading">
          {{ $t('accessibility.compliance.title', 'Accessibility Compliance') }}
        </h2>
        
        <p>
          {{ $t('accessibility.compliance.description', 'We are committed to ensuring that our website is accessible to everyone. This website has been designed and developed to conform to the European Accessibility Act (EAA) and Web Content Accessibility Guidelines (WCAG) 2.1 Level AA.') }}
        </p>

        <h3>{{ $t('accessibility.standards.title', 'Standards Compliance') }}</h3>
        <ul>
          <li>{{ $t('accessibility.standards.eaa', 'European Accessibility Act (EAA) 2025') }}</li>
          <li>{{ $t('accessibility.standards.wcag', 'Web Content Accessibility Guidelines (WCAG) 2.1 Level AA') }}</li>
          <li>{{ $t('accessibility.standards.en301549', 'EN 301 549 - Accessibility requirements for ICT procurement') }}</li>
        </ul>
      </section>

      <!-- Accessibility Features -->
      <section 
        aria-labelledby="features-heading"
        class="statement-section"
      >
        <h2 id="features-heading">
          {{ $t('accessibility.features.title', 'Accessibility Features') }}
        </h2>
        
        <h3>{{ $t('accessibility.features.keyboard.title', 'Keyboard Navigation') }}</h3>
        <ul>
          <li>{{ $t('accessibility.features.keyboard.full', 'Full keyboard navigation support without requiring a mouse') }}</li>
          <li>{{ $t('accessibility.features.keyboard.skip', 'Skip navigation links for efficient page navigation') }}</li>
          <li>{{ $t('accessibility.features.keyboard.focus', 'Visible focus indicators for all interactive elements') }}</li>
          <li>{{ $t('accessibility.features.keyboard.escape', 'Escape key closes modals and dialogs') }}</li>
        </ul>

        <h3>{{ $t('accessibility.features.screen_reader.title', 'Screen Reader Support') }}</h3>
        <ul>
          <li>{{ $t('accessibility.features.screen_reader.aria', 'Comprehensive ARIA labels and descriptions') }}</li>
          <li>{{ $t('accessibility.features.screen_reader.landmarks', 'Semantic HTML landmarks for easy navigation') }}</li>
          <li>{{ $t('accessibility.features.screen_reader.announcements', 'Live region announcements for dynamic content changes') }}</li>
          <li>{{ $t('accessibility.features.screen_reader.alt', 'Descriptive alternative text for all images') }}</li>
        </ul>

        <h3>{{ $t('accessibility.features.visual.title', 'Visual Accessibility') }}</h3>
        <ul>
          <li>{{ $t('accessibility.features.visual.contrast', 'High color contrast ratios (4.5:1 for normal text, 3:1 for large text)') }}</li>
          <li>{{ $t('accessibility.features.visual.color', 'Information conveyed through multiple means, not color alone') }}</li>
          <li>{{ $t('accessibility.features.visual.zoom', 'Support for zoom up to 400% without loss of functionality') }}</li>
          <li>{{ $t('accessibility.features.visual.motion', 'Respect for reduced motion preferences') }}</li>
        </ul>

        <h3>{{ $t('accessibility.features.forms.title', 'Form Accessibility') }}</h3>
        <ul>
          <li>{{ $t('accessibility.features.forms.labels', 'Explicit labels for all form controls') }}</li>
          <li>{{ $t('accessibility.features.forms.errors', 'Clear error messages associated with form fields') }}</li>
          <li>{{ $t('accessibility.features.forms.required', 'Required field indication') }}</li>
          <li>{{ $t('accessibility.features.forms.autocomplete', 'Autocomplete support for sensitive fields') }}</li>
        </ul>
      </section>

      <!-- Testing and Validation -->
      <section 
        aria-labelledby="testing-heading"
        class="statement-section"
      >
        <h2 id="testing-heading">
          {{ $t('accessibility.testing.title', 'Testing and Validation') }}
        </h2>
        
        <p>
          {{ $t('accessibility.testing.description', 'Our accessibility compliance is validated through:') }}
        </p>
        
        <ul>
          <li>{{ $t('accessibility.testing.automated', 'Automated testing with axe-core and Lighthouse') }}</li>
          <li>{{ $t('accessibility.testing.manual', 'Manual testing with keyboard navigation') }}</li>
          <li>{{ $t('accessibility.testing.screen_readers', 'Screen reader testing (NVDA, JAWS, VoiceOver)') }}</li>
          <li>{{ $t('accessibility.testing.users', 'Usability testing with people with disabilities') }}</li>
        </ul>

        <!-- Compliance Status -->
        <div 
          class="compliance-status"
          role="status"
          aria-live="polite"
        >
          <h3>{{ $t('accessibility.status.title', 'Current Compliance Status') }}</h3>
          <div class="status-indicator">
            <v-icon 
              color="success"
              size="24"
              aria-hidden="true"
            >
              fas fa-check-circle
            </v-icon>
            <span class="status-text">
              {{ $t('accessibility.status.compliant', 'Fully Compliant with EAA and WCAG 2.1 AA') }}
            </span>
          </div>
          <p class="status-date">
            {{ $t('accessibility.status.updated', 'Last updated:') }} 
            {{ formattedDate }}
          </p>
        </div>
      </section>

      <!-- Known Issues -->
      <section 
        aria-labelledby="issues-heading"
        class="statement-section"
      >
        <h2 id="issues-heading">
          {{ $t('accessibility.issues.title', 'Known Issues and Limitations') }}
        </h2>
        
        <div
          v-if="knownIssues.length === 0"
          class="no-issues"
        >
          <v-icon 
            color="success"
            size="20"
            aria-hidden="true"
          >
            fas fa-check
          </v-icon>
          {{ $t('accessibility.issues.none', 'No known accessibility issues at this time.') }}
        </div>
        
        <ul v-else>
          <li 
            v-for="issue in knownIssues" 
            :key="issue.id"
            class="issue-item"
          >
            <strong>{{ issue.title }}</strong>: {{ issue.description }}
            <span class="issue-status">{{ issue.status }}</span>
          </li>
        </ul>
      </section>

      <!-- Contact and Feedback -->
      <section 
        aria-labelledby="contact-heading"
        class="statement-section"
      >
        <h2 id="contact-heading">
          {{ $t('accessibility.contact.title', 'Accessibility Feedback and Contact') }}
        </h2>
        
        <p>
          {{ $t('accessibility.contact.description', 'We welcome your feedback on the accessibility of this website. Please let us know if you encounter accessibility barriers:') }}
        </p>

        <div class="contact-methods">
          <h3>{{ $t('accessibility.contact.methods', 'How to Contact Us') }}</h3>
          
          <ul>
            <li>
              <strong>{{ $t('accessibility.contact.email', 'Email:') }}</strong>
              <a href="mailto:accessibility@goz.fun">accessibility@goz.fun</a>
            </li>
            <li>
              <strong>{{ $t('accessibility.contact.phone', 'Phone:') }}</strong>
              <a href="tel:+1234567890">+1 (234) 567-8890</a>
            </li>
            <li>
              <strong>{{ $t('accessibility.contact.address', 'Postal Address:') }}</strong>
              <address>
                GOZ Gaming Platform<br>
                Accessibility Team<br>
                123 Gaming Street<br>
                Digital City, DC 12345<br>
                European Union
              </address>
            </li>
          </ul>

          <p>
            {{ $t('accessibility.contact.response', 'We aim to respond to accessibility feedback within 5 business days.') }}
          </p>
        </div>
      </section>

      <!-- Legal Information -->
      <section 
        aria-labelledby="legal-heading"
        class="statement-section"
      >
        <h2 id="legal-heading">
          {{ $t('accessibility.legal.title', 'Legal Information') }}
        </h2>
        
        <p>
          {{ $t('accessibility.legal.directive', 'This accessibility statement is prepared in accordance with the European Accessibility Act (Directive (EU) 2019/882) and applies to this website.') }}
        </p>

        <h3>{{ $t('accessibility.legal.enforcement.title', 'Enforcement Procedure') }}</h3>
        <p>
          {{ $t('accessibility.legal.enforcement.description', 'If you are not satisfied with our response to your accessibility concern, you may contact the relevant enforcement body in your EU member state.') }}
        </p>

        <h3>{{ $t('accessibility.legal.scope.title', 'Scope of this Statement') }}</h3>
        <p>
          {{ $t('accessibility.legal.scope.description', 'This statement covers the GOZ-App website and gaming platform, including all pages, features, and functionality accessible through goz.fun domain.') }}
        </p>
      </section>

      <!-- Technical Information -->
      <section 
        aria-labelledby="technical-heading"
        class="statement-section"
      >
        <h2 id="technical-heading">
          {{ $t('accessibility.technical.title', 'Technical Information') }}
        </h2>
        
        <h3>{{ $t('accessibility.technical.compatibility.title', 'Browser and Assistive Technology Compatibility') }}</h3>
        <p>
          {{ $t('accessibility.technical.compatibility.description', 'This website is designed to be compatible with the following assistive technologies:') }}
        </p>
        
        <ul>
          <li>{{ $t('accessibility.technical.browsers', 'Modern web browsers (Chrome, Firefox, Safari, Edge)') }}</li>
          <li>{{ $t('accessibility.technical.screen_readers', 'Screen readers (NVDA, JAWS, VoiceOver, TalkBack)') }}</li>
          <li>{{ $t('accessibility.technical.keyboard', 'Keyboard navigation') }}</li>
          <li>{{ $t('accessibility.technical.voice', 'Voice recognition software') }}</li>
          <li>{{ $t('accessibility.technical.zoom', 'Screen magnification software') }}</li>
        </ul>

        <h3>{{ $t('accessibility.technical.technologies.title', 'Technologies Used') }}</h3>
        <ul>
          <li>HTML5 with semantic elements</li>
          <li>CSS3 with responsive design</li>
          <li>JavaScript (Vue.js)</li>
          <li>ARIA (Accessible Rich Internet Applications)</li>
          <li>Laravel (Backend framework)</li>
        </ul>
      </section>
    </main>

    <!-- Footer with Back to Top -->
    <footer class="statement-footer">
      <back-to-top-button />
      
      <p class="statement-meta">
        {{ $t('accessibility.statement.prepared', 'This statement was prepared on') }} 
        {{ formattedDate }}
      </p>
    </footer>
  </div>
</template>

<script>
import BackToTopButton from '@/components/Views/BackToTopButton.vue';

export default {
  name: 'AccessibilityStatement',
  
  components: {
    BackToTopButton
  },

  data() {
    return {
      knownIssues: [
        // Currently no known issues - this would be populated with actual issues
        // Example:
        // {
        //   id: 1,
        //   title: 'Third-party video player',
        //   description: 'Some embedded videos may not have captions',
        //   status: 'In progress - expected resolution Q2 2025'
        // }
      ]
    };
  },

  computed: {
    /**
     * Format the current date for display
     */
    formattedDate() {
      return new Intl.DateTimeFormat(this.$i18n.locale || 'en', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(new Date());
    }
  },

  mounted() {
    // Set focus to the main heading when component mounts
    this.$nextTick(() => {
      const heading = this.$el.querySelector('#accessibility-title');
      if (heading) {
        heading.focus();
      }
    });

    // Announce page load
    if (this.$announceNavigation) {
      this.$announceNavigation('Accessibility Statement', 'Accessibility Statement');
    }
  },

  metaInfo() {
    return {
      title: this.$t('accessibility.statement.title', 'Accessibility Statement'),
      meta: [
        {
          name: 'description',
          content: this.$t('accessibility.statement.meta_description', 'Accessibility statement for GOZ-App detailing our commitment to WCAG 2.1 AA compliance and EAA standards.')
        },
        {
          name: 'robots',
          content: 'index, follow'
        }
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
@import '~/_variables.scss';

.accessibility-statement {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  line-height: 1.6;
  color: $white;
}

.statement-header {
  margin-bottom: 3rem;
  text-align: center;
  
  h1 {
    font-size: 2.5rem;
    color: $accent;
    margin-bottom: 1rem;
    font-weight: 700;
  }
  
  .statement-intro {
    font-size: 1.1rem;
    color: rgba($white, 0.9);
    max-width: 800px;
    margin: 0 auto;
  }
}

.statement-content {
  .statement-section {
    margin-bottom: 3rem;
    
    h2 {
      font-size: 1.8rem;
      color: $accent;
      margin-bottom: 1rem;
      font-weight: 600;
      border-bottom: 2px solid $accent;
      padding-bottom: 0.5rem;
    }
    
    h3 {
      font-size: 1.3rem;
      color: $white;
      margin: 1.5rem 0 0.75rem 0;
      font-weight: 500;
    }
    
    p {
      margin-bottom: 1rem;
      color: rgba($white, 0.9);
    }
    
    ul {
      margin: 1rem 0;
      padding-left: 2rem;
      
      li {
        margin-bottom: 0.5rem;
        color: rgba($white, 0.9);
        
        &::marker {
          color: $accent;
        }
      }
    }
  }
}

.compliance-status {
  background: rgba($accent, 0.1);
  border: 2px solid $accent;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  
  h3 {
    margin-top: 0;
    color: $accent;
  }
  
  .status-indicator {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1rem 0;
    
    .status-text {
      font-weight: 600;
      font-size: 1.1rem;
      color: $success;
    }
  }
  
  .status-date {
    font-size: 0.9rem;
    color: rgba($white, 0.7);
    margin: 0;
  }
}

.no-issues {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: $success;
  font-weight: 500;
  padding: 1rem;
  background: rgba($success, 0.1);
  border-radius: 4px;
  border-left: 4px solid $success;
}

.issue-item {
  background: rgba($warning, 0.1);
  border-left: 4px solid $warning;
  padding: 1rem;
  margin: 0.5rem 0;
  border-radius: 0 4px 4px 0;
  
  .issue-status {
    display: block;
    font-size: 0.9rem;
    color: rgba($white, 0.7);
    margin-top: 0.5rem;
  }
}

.contact-methods {
  background: rgba($primary, 0.5);
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  
  h3 {
    margin-top: 0;
    color: $accent;
  }
  
  ul {
    list-style: none;
    padding-left: 0;
    
    li {
      margin-bottom: 1rem;
      padding: 0.5rem 0;
      
      strong {
        color: $accent;
      }
      
      a {
        color: $white;
        text-decoration: none;
        border-bottom: 1px dotted $accent;
        
        &:hover, &:focus {
          color: $accent;
          text-decoration: none;
          border-bottom-style: solid;
        }
      }
    }
  }
  
  address {
    font-style: normal;
    margin: 1rem 0;
    padding: 1rem;
    background: rgba($secondary, 0.3);
    border-radius: 4px;
    line-height: 1.6;
  }
}

.statement-footer {
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 1px solid rgba($accent, 0.3);
  text-align: center;
  
  .statement-meta {
    margin-top: 1rem;
    font-size: 0.9rem;
    color: rgba($white, 0.6);
  }
}

// Responsive design
@media (max-width: 768px) {
  .accessibility-statement {
    padding: 1rem;
  }
  
  .statement-header {
    h1 {
      font-size: 2rem;
    }
    
    .statement-intro {
      font-size: 1rem;
    }
  }
  
  .statement-content {
    .statement-section {
      h2 {
        font-size: 1.5rem;
      }
      
      h3 {
        font-size: 1.2rem;
      }
    }
  }
  
  .contact-methods {
    padding: 1rem;
  }
}

// Print styles
@media print {
  .accessibility-statement {
    color: #000000 !important;
    background: #ffffff !important;
  }
  
  .statement-header h1,
  .statement-section h2,
  .statement-section h3 {
    color: #000000 !important;
  }
  
  .compliance-status,
  .contact-methods {
    background: #f5f5f5 !important;
    border: 1px solid #cccccc !important;
  }
  
  a {
    color: #000000 !important;
    text-decoration: underline !important;
  }
}

// High contrast mode
@media (prefers-contrast: high) {
  .accessibility-statement {
    background: #000000 !important;
    color: #ffffff !important;
  }
  
  .statement-header h1,
  .statement-section h2,
  .contact-methods h3 {
    color: #ffffff !important;
  }
  
  .compliance-status,
  .contact-methods {
    background: #000000 !important;
    border: 2px solid #ffffff !important;
  }
  
  a {
    color: #ffffff !important;
    background: #0000ff !important;
    padding: 2px 4px !important;
  }
}

// Reduced motion
@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
    animation: none !important;
  }
}
</style>